// Swipeable persona card. Supports touch and mouse drag; the topmost card in
// the stack is interactive and the others render as a subtle pile.
//
// A11y: the card is reachable by keyboard — left/right arrows or A/D fire
// onSwipe so users can drive the deck without gestures.

import { useEffect, useRef, useState } from 'react';
import { GraduationCap } from 'lucide-react';
import type { Persona } from '@/lib/types';
import { Portrait } from '@/components/portraits';
import { SealStamp } from '@/components/visual/SealStamp';
import { InkMountains } from '@/components/visual/InkMountains';
import { C } from '@/styles/tokens';

export type SwipeDirection = 'left' | 'right';

interface SwipeCardProps {
  persona: Persona;
  isTop: boolean;
  stackOffset: number;
  onSwipe: (dir: SwipeDirection) => void;
}

interface DragState {
  x: number;
  y: number;
  dragging: boolean;
}

export function SwipeCard({ persona, isTop, stackOffset, onSwipe }: SwipeCardProps) {
  const [drag, setDrag] = useState<DragState>({ x: 0, y: 0, dragging: false });
  const startRef = useRef({ x: 0, y: 0 });

  // Keyboard nav for the top card.
  useEffect(() => {
    if (!isTop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') onSwipe('right');
      else if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') onSwipe('left');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isTop, onSwipe]);

  function pointFromEvent(e: React.MouseEvent | React.TouchEvent): { clientX: number; clientY: number } {
    if ('touches' in e && e.touches.length > 0) {
      const t = e.touches[0]!;
      return { clientX: t.clientX, clientY: t.clientY };
    }
    const m = e as React.MouseEvent;
    return { clientX: m.clientX, clientY: m.clientY };
  }

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isTop) return;
    const point = pointFromEvent(e);
    startRef.current = { x: point.clientX, y: point.clientY };
    setDrag({ x: 0, y: 0, dragging: true });
  };

  const handleMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drag.dragging) return;
    const point = pointFromEvent(e);
    setDrag({
      x: point.clientX - startRef.current.x,
      y: point.clientY - startRef.current.y,
      dragging: true,
    });
  };

  const handleEnd = () => {
    if (!drag.dragging) return;
    const threshold = 90;
    if (drag.x > threshold) {
      setDrag({ x: 600, y: drag.y, dragging: false });
      window.setTimeout(() => onSwipe('right'), 250);
    } else if (drag.x < -threshold) {
      setDrag({ x: -600, y: drag.y, dragging: false });
      window.setTimeout(() => onSwipe('left'), 250);
    } else {
      setDrag({ x: 0, y: 0, dragging: false });
    }
  };

  const rot = drag.x * 0.06;
  const likeOpacity = Math.max(0, Math.min(1, drag.x / 100));
  const nopeOpacity = Math.max(0, Math.min(1, -drag.x / 100));
  const transform = isTop
    ? `translate(${drag.x}px, ${drag.y}px) rotate(${rot}deg)`
    : `translateY(${stackOffset}px) scale(${1 - stackOffset / 60})`;
  const transition = drag.dragging ? 'none' : 'transform 0.35s cubic-bezier(.2,.8,.3,1)';

  return (
    <div
      role={isTop ? 'group' : 'presentation'}
      aria-label={isTop ? `${persona.en}, ${persona.age}, ${persona.cityEn}` : undefined}
      aria-roledescription={isTop ? 'swipeable card' : undefined}
      tabIndex={isTop ? 0 : -1}
      className="absolute inset-0 select-none"
      style={{ transform, transition, zIndex: isTop ? 10 : 5, touchAction: 'none' }}
      onMouseDown={handleStart}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
    >
      <div
        className="relative w-full h-full rounded-[28px] overflow-hidden shadow-2xl flex flex-col ff-body"
        style={{ background: C.cream, cursor: isTop ? 'grab' : 'default' }}
      >
        <div className="relative" style={{ height: '46%', flexShrink: 0 }}>
          <Portrait persona={persona} />
          {/* Drifting ink-wash mountains across the portrait's lower edge. */}
          <InkMountains
            height={80}
            className="absolute left-0 right-0 bottom-0"
            tint="#1A1614"
          />
          <SealStamp char="字" className="absolute top-4 right-4" />
          <div
            className="absolute inset-x-0 bottom-0 h-28 pointer-events-none"
            style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}
          />
          <div className="absolute bottom-3 left-5 right-5 text-white">
            <div className="flex items-baseline gap-2.5">
              <span className="ff-zh-serif text-3xl font-bold tracking-tight leading-none">{persona.name}</span>
              <span className="ff-display text-2xl italic opacity-90 leading-none">{persona.age}</span>
            </div>
            <div className="ff-body text-[11px] tracking-wider opacity-90 mt-1">
              {persona.pinyin} · {persona.cityEn}
            </div>
          </div>
          {/* Brush calligraphy verdict overlays — raw ink on the photo
              rather than framed stickers. Scale up on commitment. */}
          <div
            aria-hidden
            className="absolute top-8 left-5 ff-zh-serif font-black tracking-tighter rotate-[-14deg]"
            style={{
              color: C.jade,
              opacity: likeOpacity,
              fontSize: 64,
              lineHeight: 1,
              textShadow: '0 2px 8px rgba(0,0,0,0.35)',
              transform: `rotate(-14deg) scale(${0.85 + likeOpacity * 0.25})`,
              transformOrigin: 'left top',
            }}
          >
            喜欢
          </div>
          <div
            aria-hidden
            className="absolute top-8 right-5 ff-zh-serif font-black tracking-tighter rotate-[14deg]"
            style={{
              color: C.vermillion,
              opacity: nopeOpacity,
              fontSize: 64,
              lineHeight: 1,
              textShadow: '0 2px 8px rgba(0,0,0,0.35)',
              transform: `rotate(14deg) scale(${0.85 + nopeOpacity * 0.25})`,
              transformOrigin: 'right top',
            }}
          >
            过
          </div>
        </div>

        <div className="flex-1 px-5 pt-3 pb-3 flex flex-col gap-2.5 overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-[0.18em] flex-1 truncate" style={{ color: C.inkLight }}>
              {persona.occupation}
            </div>
            <div
              className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider"
              style={{ background: C.blush, color: C.vermillionDeep }}
            >
              <GraduationCap size={10} aria-hidden />
              {persona.level.tag}
            </div>
          </div>

          <div className="ff-zh-serif text-[17px] leading-snug font-medium" style={{ color: C.ink }}>
            <span style={{ color: C.vermillion }}>「</span>
            {persona.bio}
            <span style={{ color: C.vermillion }}>」</span>
          </div>

          <div className="ff-body text-[12px] leading-relaxed" style={{ color: C.inkLight }}>
            {persona.intro}
          </div>

          <div className="mt-auto">
            <div
              className="ff-body text-[9px] uppercase tracking-[0.25em] mb-1.5 inline-flex items-center gap-1.5"
              style={{ color: C.inkSofter }}
            >
              <span style={{ color: C.vermillion }} aria-hidden>◆</span>
              Expect to learn
            </div>
            <div className="flex flex-wrap gap-1.5">
              {persona.topics.map((t) => (
                <div
                  key={t.hz}
                  className="flex items-baseline gap-1.5 px-2.5 py-1 rounded-full"
                  style={{ background: C.cream, border: `1px solid ${C.mist}` }}
                >
                  <span className="ff-zh-serif text-[14px] font-medium" style={{ color: C.ink }}>
                    {t.hz}
                  </span>
                  <span className="ff-body text-[10px] opacity-60" style={{ color: C.ink }}>
                    {t.py}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
